/**
 * transport/files.ts —— **文件读面客户端**（★F3d；`GET /api/files`，按会话可寻址）。
 *
 * 用途：中栏附件条目显示**文件名**——读面（装配事实）按设计只带**引用**（`file_id`），
 *   文件名由本件经既有读面取回，前端建 `id → filename` 映射后交给行还原（引用不复制内容的
 *   呈现侧落法；登记字段唯一源仍是 `wf.file`）。
 * 纪律：路径经 `assertEgressQuery`（同源相对 + 键白名单）；失败**显式抛**（禁静默假成功）；
 *   缺名不造（调用方回退 `file_id`）。
 */
import { assertEgressQuery, assertEgressWithin } from "./whitelist";
import type { FetchLike } from "./session";
import { E_CODES } from "./errorText";

export interface FileItem {
  file_id?: string | number;
  id?: string | number;
  filename?: string;
  name?: string;
  size?: number | string;
  source_kind?: string;
  content_type?: string;
}

export interface FileListPage {
  items: FileItem[];
  total?: number;
  offset?: number;
  limit?: number;
  truncated?: boolean;
}

export class FilesError extends Error {
  readonly status: number;
  constructor(status: number, message: string) {
    super(E_CODES.FILES_FAILED + "：" + message);
    this.name = "FilesError";
    this.status = status;
  }
}

export async function listFiles(
  fetchFn: FetchLike = fetch,
  opts: { sessionId?: string; source?: string; offset?: number; limit?: number } = {},
): Promise<FileListPage> {
  const path = assertEgressQuery("files", "/api/files", {
    session_id: opts.sessionId,
    source: opts.source,
    offset: opts.offset,
    limit: opts.limit ?? 200,
  });
  const res = await fetchFn(path, { method: "GET", credentials: "same-origin" });
  if (!res.ok) throw new FilesError(res.status, "文件列表被拒（HTTP " + res.status + "）");
  const data = (await res.json()) as FileListPage | null;
  return {
    items: Array.isArray(data?.items) ? (data as FileListPage).items : [],
    total: data?.total,
    offset: data?.offset,
    limit: data?.limit,
    truncated: data?.truncated,
  };
}

/**
 * `id → 文件名`（**只取登记字段**；缺 `filename` ⇒ 该条不映射，调用方回退 `file_id`）。
 *
 * 🔴 **键形归一**：读面 `to_entry` 出的是 **uuid**（带横线），而装配事实里的附件引用是
 * **32 位 hex**（不带横线）——不归一则键对不上、映射恒不命中（20261006 真机实锤一次）。
 * 故键一律 `去横线+小写`（与 `file_id` 句柄同形）。
 */
export function fileNames(page: FileListPage): Record<string, string> {
  const out: Record<string, string> = {};
  for (const item of page.items ?? []) {
    const id = String(item.file_id ?? item.id ?? "").replace(/-/g, "").trim().toLowerCase();
    const name = String(item.filename ?? item.name ?? "").trim();
    if (id && name) out[id] = name;
  }
  return out;
}


/** 文本预览**分页**读面（设计件 §6.5 模式一：`GET /api/files/{id}/pages`，累计前缀 + `eof`）。
 *  纪律：路径经 `assertEgressWithin`（同源相对 + 单件路径）；失败**显式抛**；超限由后端**拒绝**（不截断）。 */
export interface FilePage {
  file_id: string;
  offset: number;
  limit: number;
  lines: string[];
  next_offset: number | null;
  eof: boolean;
  total_bytes: number;
}

export async function readFilePages(
  fetchImpl: FetchLike,
  fileId: string,
  opts: { offset?: number; limit?: number } = {},
): Promise<FilePage> {
  const offset = opts.offset ?? 0;
  const limit = opts.limit ?? 2000;
  // 单件路径走 `assertEgressWithin`（同源相对 + 白名单集合内）；查询值**只许数字**（禁注入面）
  const base = assertEgressWithin("files", `/api/files/${encodeURIComponent(fileId)}/pages`);
  const path = `${base}?offset=${Number(offset) || 0}&limit=${Number(limit) || 2000}`;
  const res = await fetchImpl(path, { method: "GET", credentials: "same-origin" });
  if (!res.ok) throw new FilesError(res.status, "预览读面被拒（HTTP " + res.status + "）");
  const data = (await res.json().catch(() => null)) as FilePage | null;
  if (!data || !Array.isArray(data.lines)) throw new FilesError(res.status, "预览读面返回形态不符");
  return data;
}

/** 从地址取 file_id（`sononux://file/session/<sid>/id/<file_id>`；非本形 ⇒ 空串，不猜） */
export function fileIdFromAddress(address: string): string {
  const m = /sononux:\/\/file\/session\/([^/?#]+)\/id\/([^/?#]+)/.exec(address || "");
  return m ? m[2] : "";
}
